import React, {createContext, useReducer, ReactElement, Reducer} from "react";
import {IDismissNotificationProps, INotification, INotificationContext, INotificationSeverity as Severity, INotifyContextAction, INotifyProps} from "./types/INotification";

const handleInitError = (): void => {throw new Error("Notification Context not initialized, check library dependency. Likely issue with multiple context providers.")};

export const NotificationContext = createContext<INotificationContext>({
	notifications: [],
	notify: handleInitError,
	clearNotifications: handleInitError,
	dismissNotification: handleInitError
});

export interface IProps {
	children: ReactElement
}

const NotificationProvider = (props: IProps): JSX.Element => {
	const [notifications, notifyDispatch] = useReducer<Reducer<INotification[], INotifyContextAction>>((prevState, action) => {
		switch (action.type) {
			case "clear":
				return [];
			case "remove":
				if(action?.payload?.id) prevState = prevState.filter(e => e.id !== action?.payload?.id);
				return [...prevState];
			case "add":
			default: {
				if (!window) return prevState;
				const id = window.performance.now() + prevState.length;
				const newMsg = {...action.payload, id} as INotification;

				//remove after lifeTime if present
				if(action?.payload?.lifeTime){
					setTimeout(() => {
						notifyDispatch({type: "remove", payload: newMsg});
					}, action.payload.lifeTime);
				}
				return [...prevState, newMsg];
			}
		}
	}, []);

	const notify:INotifyProps = (body, title = "", severity = Severity.success, lifeTime = 5000) =>
		notifyDispatch({payload: {title, body, severity, lifeTime, timeAdded: new Date()}});

	const dismissNotification: IDismissNotificationProps = (notification) =>
		notifyDispatch({type: "remove", payload: notification})

	const clearNotifications = (): void =>
		notifyDispatch({type: "clear"})

	return (
		<NotificationContext.Provider value={{notifications, notify, dismissNotification, clearNotifications}}>
			{props.children}
		</NotificationContext.Provider>
	);
};

export default NotificationProvider;
